import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import type { LeaderboardEntry } from "../../types/core";
import { HEX } from "../../lib/colorSystem";

// =============================================================================
// SETTER SNIPPET — Bento Dashboard Card
// =============================================================================
// PURPOSE: Compact hero cards for each setter showing key DM/booking stats.
// Uses already-loaded leaderboard data (no extra API calls).
// =============================================================================

interface SetterSnippetProps {
  leaderboard: LeaderboardEntry[];
  dateRange: { start: Date | null; end: Date | null };
}

interface AggSetter {
  rep_id: string;
  rep_name: string;
  calls_booked: number;
  shows: number;
  no_shows: number;
  show_rate: number;
  /** Daily booking breakdown for sparkline */
  dailyBookings: number[];
}

const SetterSnippet: React.FC<SetterSnippetProps> = ({ leaderboard }) => {
  const setters = useMemo(() => {
    // Filter to setter sector only
    const setterEntries = leaderboard.filter((e) => e.sector === "setter");
    if (setterEntries.length === 0) return [];

    // Aggregate per setter across dates
    const byRep = new Map<string, { entries: LeaderboardEntry[] }>();
    setterEntries.forEach((e) => {
      const existing = byRep.get(e.rep_id);
      if (existing) {
        existing.entries.push(e);
      } else {
        byRep.set(e.rep_id, { entries: [e] });
      }
    });

    const result: AggSetter[] = [];
    byRep.forEach((data, repId) => {
      const entries = data.entries;
      const totalBooked = entries.reduce((s, e) => s + e.calls_booked, 0);
      const totalShows = entries.reduce((s, e) => s + e.shows, 0);
      const totalNoShows = entries.reduce((s, e) => s + e.no_shows, 0);
      const totalOrphans = entries.reduce((s, e) => s + (e.orphan_precalls || 0), 0);
      // SHOW rate (R14, Mako). shows / (shows + no_shows + orphan_precalls) — never calls_booked.
      const showDenominator = totalShows + totalNoShows + totalOrphans;
      const showRate = showDenominator > 0 ? totalShows / showDenominator : 0;

      // Build daily bookings for sparkline (sorted by date)
      const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date));
      const dailyBookings = sorted.map((e) => e.calls_booked);

      result.push({
        rep_id: repId,
        rep_name: entries[0].rep_name,
        calls_booked: totalBooked,
        shows: totalShows,
        no_shows: totalNoShows,
        show_rate: showRate,
        dailyBookings,
      });
    });

    // Sort by calls_booked descending
    result.sort((a, b) => b.calls_booked - a.calls_booked);
    return result;
  }, [leaderboard]);

  if (setters.length === 0) {
    return (
      <div className="liquid-glass anim-lift p-4 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="token-14s tracking-wider text-glass-muted">Setter Performance</h3>
        </div>
        <p className="token-11 text-glass-muted text-center py-4">No setter data this period</p>
      </div>
    );
  }

  return (
    <div className="liquid-glass anim-lift p-4 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="token-14s tracking-wider text-glass-muted">Setter Performance</h3>
        <Link
          to="/admin/dm-analytics"
          className="token-11 text-blue-400 hover:text-blue-300 transition-colors"
        >
          View all →
        </Link>
      </div>

      {/* Setter Cards */}
      <div className="space-y-2 flex-1 min-h-0">
        {setters.map((setter) => (
          <SetterCard key={setter.rep_id} setter={setter} />
        ))}
      </div>
    </div>
  );
};

// ─── Individual Setter Card ─────────────────────────────────────────────────

function SetterCard({ setter }: { setter: AggSetter }) {
  const firstName = setter.rep_name.split(" ")[0];
  const maxBooking = Math.max(...setter.dailyBookings, 1);

  return (
    <div className="rounded-lg px-3 py-2.5" style={{ background: "var(--glass-bg-hover)" }}>
      {/* Name + sparkline row */}
      <div className="flex items-center justify-between mb-1.5">
        <span className="token-13 font-semibold text-glass">{firstName}</span>
        {/* Mini bar sparkline */}
        <div className="flex items-end gap-[2px] h-[16px]">
          {setter.dailyBookings.slice(-7).map((val, i) => (
            <div
              key={i}
              className="rounded-sm"
              style={{
                width: 4,
                height: Math.max(2, (val / maxBooking) * 16),
                background:
                  val > 0
                    ? `color-mix(in srgb, ${HEX.accent} ${40 + (val / maxBooking) * 60}%, transparent)`
                    : "var(--glass-bg-active)",
              }}
            />
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-3">
        <StatPill label="Booked" value={setter.calls_booked} color={HEX.accent} />
        <StatPill label="Shown" value={setter.shows} color={HEX.green} />
        <StatPill label="No-Show" value={setter.no_shows} color={HEX.red} />
        <StatPill
          label="Show %"
          value={`${(setter.show_rate * 100).toFixed(0)}%`}
          color={
            setter.show_rate >= 0.7 ? HEX.green : setter.show_rate >= 0.5 ? HEX.amber : HEX.red
          }
        />
      </div>
    </div>
  );
}

function StatPill({
  label,
  value,
  color,
}: {
  label: string;
  value: string | number;
  color: string;
}) {
  return (
    <div className="flex flex-col items-center min-w-0">
      <span className="token-9 uppercase tracking-wide text-glass-muted">{label}</span>
      <span className="token-13 font-bold" style={{ color }}>
        {value}
      </span>
    </div>
  );
}

export default SetterSnippet;
